import {
  ChatsContext_MarkChatViewedMutation,
  ChatsContext_MarkChatViewedMutationVariables,
  ChatsContext_UnreadCountSubscription,
  ChatsContext_UnreadCountSubscriptionVariables,
} from '@/types';
import { gql, useMutation } from '@apollo/client';
import { partnerSupplier } from '@/utils/environment';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSubscription } from '@/contexts/subscription-context';

export interface ChatUnreadCountMap {
  [chatUUID: string]: number;
}

export const MARK_CHAT_VIEWED_MUTATION = gql`
  mutation ChatsContext_MarkChatViewed(
    $chatUUID: uuid!
    $lastMessageTimestamp: String!
  ) {
    viewMessage(
      chat_id: $chatUUID
      last_viewed_message_timestamp: $lastMessageTimestamp
    ) {
      success
    }
  }
`;

export const CHAT_UNREAD_COUNT_SUBSCRIPTION = gql`
  subscription ChatsContext_UnreadCount(
    $businessOrganizationUUID: uuid!
    $userUUID: uuid!
  ) {
    chat_unread_message_count(
      where: {
        business_organization_uuid: { _eq: $businessOrganizationUUID }
        employee_uuid: { _eq: $userUUID }
      }
    ) {
      chat_uuid
      unread_message_count
    }
  }
`;

export const useChatUnreadCount = () => {
  const { user } = useAuthenticatedUserContext();

  const isMarkingChatViewedRef = useRef(false);
  const [chatUnreadCountMap, setChatUnreadCountMap] =
    useState<ChatUnreadCountMap>({});

  useSubscription<
    ChatsContext_UnreadCountSubscription,
    ChatsContext_UnreadCountSubscriptionVariables
  >(CHAT_UNREAD_COUNT_SUBSCRIPTION, {
    fetchPolicy: 'no-cache',
    onData: ({ data: subscriptionData }) => {
      const updatedChatUnreadCountMap = (
        subscriptionData.data?.chat_unread_message_count || []
      ).reduce<ChatUnreadCountMap>((result, ce) => {
        if (ce.chat_uuid && typeof ce.unread_message_count === 'number') {
          result[ce.chat_uuid] = ce.unread_message_count;
        }

        return result;
      }, {});

      setChatUnreadCountMap(updatedChatUnreadCountMap);
    },
    variables: {
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      userUUID: user.uuid,
    },
  });

  const [markChatViewed] = useMutation<
    ChatsContext_MarkChatViewedMutation,
    ChatsContext_MarkChatViewedMutationVariables
  >(MARK_CHAT_VIEWED_MUTATION);

  const markChatViewedCallback = useCallback(
    async (chatUUID: string, lastMessageTimestamp: string) => {
      if (isMarkingChatViewedRef.current) {
        return;
      }

      isMarkingChatViewedRef.current = true;

      try {
        await markChatViewed({
          variables: {
            chatUUID,
            lastMessageTimestamp,
          },
        });

        setChatUnreadCountMap((map) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [chatUUID]: _, ...rest } = map;
          return rest;
        });
      } catch (e) {
        // Do nothing
      }

      isMarkingChatViewedRef.current = false;
    },
    [markChatViewed],
  );

  return useMemo(
    () => ({
      chatUnreadCountMap,
      markChatViewed: markChatViewedCallback,
    }),
    [chatUnreadCountMap, markChatViewedCallback],
  );
};
